import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import api from '@/lib/api'

interface User {
  id: number
  username: string
  email: string
  full_name: string | null
  is_admin: boolean
  permissions: string[]
}

interface PendingVerification {
  user_id: number
  email_masked: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  pendingVerification: PendingVerification | null
  login: (username: string, password: string) => Promise<{ requiresVerification: boolean }>
  verifyCode: (code: string) => Promise<void>
  resendCode: () => Promise<void>
  cancelVerification: () => void
  logout: () => void
  hasPermission: (section: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = 'jvs_auth_token'
const USER_KEY = 'jvs_auth_user'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null)

  // Restaurar sesión del localStorage al cargar
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setToken(storedToken)
        setUser(parsedUser)

        // Verificar que el token sigue siendo válido
        validateToken(storedToken)
      } catch {
        // Token o usuario inválido, limpiar
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
      }
    }

    setIsLoading(false)
  }, [])

  const validateToken = async (currentToken: string) => {
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${currentToken}` }
      })
      // Token válido, actualizar usuario con datos frescos
      const freshUser = response.data
      setUser(freshUser)
      localStorage.setItem(USER_KEY, JSON.stringify(freshUser))
    } catch {
      // Token inválido o expirado
      logout()
    }
  }

  const login = useCallback(async (username: string, password: string): Promise<{ requiresVerification: boolean }> => {
    const response = await api.post('/auth/login', { username, password })
    const data = response.data

    // Check if 2FA verification is required
    if (data.requires_verification) {
      setPendingVerification({
        user_id: data.user_id,
        email_masked: data.email_masked
      })
      return { requiresVerification: true }
    }

    // Direct login (shouldn't happen with 2FA enabled, but just in case)
    const { access_token, user: userData } = data
    setToken(access_token)
    setUser(userData)
    localStorage.setItem(TOKEN_KEY, access_token)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
    return { requiresVerification: false }
  }, [])

  const verifyCode = useCallback(async (code: string) => {
    if (!pendingVerification) {
      throw new Error('No hay verificación pendiente')
    }

    const response = await api.post('/auth/verify-code', {
      user_id: pendingVerification.user_id,
      code: code
    })

    const { access_token, user: userData } = response.data

    // Clear pending verification
    setPendingVerification(null)

    // Set authenticated state
    setToken(access_token)
    setUser(userData)
    localStorage.setItem(TOKEN_KEY, access_token)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
  }, [pendingVerification])

  const resendCode = useCallback(async () => {
    if (!pendingVerification) {
      throw new Error('No hay verificación pendiente')
    }

    await api.post('/auth/resend-code', {
      user_id: pendingVerification.user_id
    })
  }, [pendingVerification])

  const cancelVerification = useCallback(() => {
    setPendingVerification(null)
  }, [])

  const logout = useCallback(() => {
    // Intentar llamar al endpoint de logout (no bloqueante)
    if (token) {
      api.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {
        // Ignorar errores de logout
      })
    }

    // Limpiar estado y localStorage
    setToken(null)
    setUser(null)
    setPendingVerification(null)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }, [token])

  const hasPermission = useCallback((section: string): boolean => {
    if (!user) return false
    if (user.is_admin) return true

    // Verificar permiso exacto o permiso padre
    return user.permissions.some(perm =>
      section === perm || section.startsWith(`${perm}.`)
    )
  }, [user])

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    pendingVerification,
    login,
    verifyCode,
    resendCode,
    cancelVerification,
    logout,
    hasPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Hook para requerir autenticación
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth()
  return { isAuthenticated, isLoading }
}

// Hook para verificar permiso específico
export function usePermission(section: string) {
  const { hasPermission, isAuthenticated, isLoading } = useAuth()
  return {
    hasPermission: hasPermission(section),
    isAuthenticated,
    isLoading
  }
}
